import { useNotification, usePermissions } from 'hooks'
import { useMemo, useState } from 'react'
import { getColumns, getFilters } from './helpers'
import { AddActionIcon, DeleteActionIcon, EditActionIcon } from 'icons'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import s from './CollectingCompanies.modules.scss'
import { Typography } from '@mui/material'
import { Table } from 'components/core-ui/Table'
import { CollectingCompanyModal } from './CollectingCompanyModal'
import { collectingCompaniesRoles } from '../constants'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { CollectingCompanyTableItem } from '../interfaces'
import { getWasteCategoryOptions } from '../api'
import { LocationsModal } from './LocationsModal'

const CollectingCompanies = () => {
  const [tableItems, setTableItems] = useState<CollectingCompanyTableItem[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<CollectingCompanyTableItem>()
  const [shouldRefetchItem, setShouldRefetchItem] = useState(false)

  const { showNotification } = useNotification()

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const selectRow = (rowId: number, action: string) => {
    const item = tableItems.find(item => item.id === rowId)
    setSelectedRow(item)

    switch (action) {
      case 'edit':
        openEditModal()
        break
      case 'delete':
        openDeleteModal()
        break
      case 'viewLocations':
        openLocationsModal()
        break
      default:
        break
    }
  }

  const columns = useMemo(() => getColumns(selectRow), [tableItems])

  const filters = useMemo(() => getFilters(wasteCategoryOptions), [wasteCategoryOptions])

  const { canCreate, canEdit, canDelete } = usePermissions(collectingCompaniesRoles)

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => {
    setIsEditModalOpen(true)
    setShouldRefetchItem(true)
  }
  const closeEditModal = (): void => {
    setIsEditModalOpen(false)
    setShouldRefetchItem(false)
  }

  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const openLocationsModal = (): void => setIsLocationsModalOpen(true)
  const closeLocationsModal = (): void => setIsLocationsModalOpen(false)

  const toolbarActions = useMemo(() => {
    if (canCreate) {
      return [
        {
          label: 'Adauga',
          icon: <AddActionIcon />,
          onClick: () => openNewModal()
        }
      ]
    }

    return []
  }, [canCreate])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/company',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru companiile colectoare. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  return (
    <div className={s.mainContainer}>
      {canCreate && (
        <CollectingCompanyModal
          isModalOpen={isNewModalOpen}
          closeModal={closeNewModal}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canEdit && (
        <CollectingCompanyModal
          isModalOpen={isEditModalOpen}
          closeModal={closeEditModal}
          editItemId={selectedRow?.id}
          shouldRefetchItem={shouldRefetchItem}
          setShouldRefreshData={setShouldRefreshData}
          isEdit
        />
      )}
      {canDelete && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          actionUrl="/waste-management-system/company/:id"
          itemId={`${selectedRow?.id}`}
          content={<Typography>Sunteti sigur ca vreti sa stergeti aceasta companie?</Typography>}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      <LocationsModal
        isModalOpen={isLocationsModalOpen}
        closeModal={closeLocationsModal}
        companyId={selectedRow?.id}
      />
      <Table
        hideCheckbox
        orderBy="name"
        order="asc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          canEdit && {
            label: 'Editeaza',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <EditActionIcon />,
            onClick: (_e, rowId) => selectRow(rowId, 'edit'),
            showTooltip: true
          },
          canDelete && {
            label: 'Sterge',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#d9534f',
            desktopIcon: <DeleteActionIcon />,
            onClick: (_e, rowId) => selectRow(rowId, 'delete'),
            showTooltip: true
          }
        ]}
        rowActionsWidth="200px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
      />
    </div>
  )
}

export default CollectingCompanies
